import React from "react";
import { Box, Typography, Chip } from "@mui/material";
import { Avatar } from "@mui/material";
import styles from "./teamLeaderboard.module.scss";

interface LeaderboardEntry {
  rank: number;
  name: string;
  initials: string;
  poCount: number;
  avgPct: number;
  savings: number;
  avatarColor?: string;
}

interface TeamLeaderboardProps {
  title?: string;
  data?: LeaderboardEntry[];
  periodLabel?: string;
}

const TeamLeaderboard: React.FC<TeamLeaderboardProps> = ({
  title = "Team Leaderboard",
  data = [],
  periodLabel = "This Month",
}) => {
  const getAvatarColor = (rank: number): string => {
    const colors = [
      "#00d2aa", // teal
      "#5b6b9e", // blue
      "#ffa94d", // orange
      "#7b8fc4", // light blue
      "#00d2aa", // teal
    ];
    return colors[rank - 1] || "#5b6b9e";
  };

  const getRankStyle = (rank: number) => {
    if (rank === 1) return styles.rankGold;
    if (rank === 2) return styles.rankSilver;
    if (rank === 3) return styles.rankBronze;
    return styles.rankDefault;
  };

  const getRowStyle = (rank: number) => {
    if (rank <= 3) return styles.topPerformer;
    return "";
  };

  const formatSavings = (value: number): string => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(0)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value}`;
  };

  if (!data || data.length === 0) {
    return (
      <Box className={styles.container}>
        <Box className={styles.header}>
          <Typography variant="h6" className={styles.title}>
            🏆 {title}
          </Typography>
          {periodLabel && (
            <Chip label={periodLabel} size="small" className={styles.periodChip} />
          )}
        </Box>
        <Box
          className={styles.leaderboardContainer}
          display="flex"
          alignItems="center"
          justifyContent="center"
          minHeight={200}
        >
          <Typography variant="body2" color="textSecondary">
            No data available
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={styles.container}>
      <Box className={styles.header}>
        <Typography variant="h6" className={styles.title}>
          🏆 {title}
        </Typography>
        {periodLabel && (
          <Chip label={periodLabel} size="small" className={styles.periodChip} />
        )}
      </Box>

      <Box className={styles.leaderboardContainer}>
        {data.map((entry) => (
          <Box
            key={entry.rank}
            className={`${styles.leaderboardRow} ${getRowStyle(entry.rank)}`}
          >
            <Box className={`${styles.rank} ${getRankStyle(entry.rank)}`}>
              {entry.rank}
            </Box>
            <Avatar
              className={styles.avatar}
              sx={{
                backgroundColor: entry.avatarColor || getAvatarColor(entry.rank),
              }}
            >
              {entry.initials}
            </Avatar>
            <Box className={styles.userInfo}>
              <Typography className={styles.userName}>{entry.name}</Typography>
              <Typography className={styles.userStats}>
                {entry.poCount} POs • Avg PCT: {entry.avgPct} days
              </Typography>
            </Box>
            <Box className={styles.savingsInfo}>
              <Typography className={styles.savingsAmount}>
                {formatSavings(entry.savings)}
              </Typography>
              <Typography className={styles.savingsLabel}>Savings</Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default TeamLeaderboard;
